import {Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ImportContainer} from 'app/Model/dataProcess/importContainer';
import {Catalog} from 'app/Model/Catalog/Catalog';
import {CatalogService} from 'app/Services/CatalogManagement/catalog.service';
import {LoginService} from 'app/Services/login.service';
import {SignalrService} from 'app/Services/signalr.service';
import {ComparisonCounts, ImportContainerService} from 'app/Services/DataProcess/import-container.service';

@Component({
  selector: 'np-inspect-container-catalog',
  templateUrl: './inspect-container-catalog.component.html',
  styleUrls: ['./inspect-container-catalog.component.css'],
  standalone: false
})
export class InspectContainerCatalogComponent implements OnInit, OnChanges {

  @Input() container: ImportContainer | null = null;
  @Input() catalogId: string | null = null;
  @Input() dataSource: any | null = null;
  @Input() showContainerTab = true;

  @Output() containerSaved = new EventEmitter<ImportContainer>();

  catalog: Catalog | null = null;
  lastImportedCatalogName: string = '';
  comparisonCounts: ComparisonCounts | null = null;
  jobsKey: string | null = null;

  isLoading = false;
  hasError = false;
  editVisible = false;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly catalogService: CatalogService,
    private readonly loginService: LoginService,
    private readonly signalrService: SignalrService,
    private readonly importContainerService: ImportContainerService
  ) {}

  ngOnInit(): void {
    this.signalrService.refreshCatalogList$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadCatalog();
        this.loadComparisonCounts();
      });

    this.signalrService.refreshComparison$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ catalogId }) => {
        if (catalogId && catalogId === this.catalogId) {
          this.loadCatalog();
          this.loadComparisonCounts();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['catalogId']) {
      this.jobsKey = null;
      this.editVisible = false;
      this.loadCatalog();
      this.loadComparisonCounts();
    }
    if (changes['container']) {
      this.resolveLastImportedCatalogName();
    }
  }

  private async resolveLastImportedCatalogName(): Promise<void> {
    const ids = this.container?.catalogIds;
    if (!ids?.length) {
      this.lastImportedCatalogName = '';
      return;
    }
    const lastId = ids[ids.length - 1];
    const customerId = this.loginService?.currentUser?.customerId;
    if (!lastId || !customerId) {
      this.lastImportedCatalogName = '';
      return;
    }
    try {
      this.lastImportedCatalogName = await this.catalogService.getCatalogName(lastId, customerId) ?? '';
    } catch {
      this.lastImportedCatalogName = '';
    }
  }

  private async loadComparisonCounts(): Promise<void> {
    const catalogId = this.catalogId;
    const customerId = this.loginService?.currentUser?.customerId;

    if (!catalogId || !customerId) {
      this.comparisonCounts = null;
      return;
    }

    try {
      const counts = await this.importContainerService.getComparisonCounts(catalogId, customerId);
      if (this.catalogId === catalogId) {
        this.comparisonCounts = counts;
      }
    } catch {
      if (this.catalogId === catalogId) {
        this.comparisonCounts = null;
      }
    }
  }

  private loadCatalog(): void {
    const id = this.catalogId;
    const customerId = this.loginService?.currentUser?.customerId;

    if (!id || !customerId) {
      this.catalog = null;
      this.jobsKey = null;
      this.isLoading = false;
      this.hasError = false;
      return;
    }

    this.isLoading = true;
    this.hasError = false;

    this.catalogService.getCatalogNoSet(id, customerId).subscribe({
      next: (c: Catalog) => {
        if (this.catalogId !== id) {
          return;
        }
        this.catalog = c;
        this.isLoading = false;
        this.catalogService.selectedCatalogId = c.id;
        this.catalogService.catalog = c;
        this.jobsKey = c.id;
      },
      error: () => {
        if (this.catalogId !== id) {
          return;
        }
        this.catalog = null;
        this.isLoading = false;
        this.hasError = true;
      }
    });
  }

  openMetadataDialog(): void {
    if (!this.catalog) {
      return;
    }
    this.editVisible = true;
  }

  onMetadataSaved(updated: Catalog): void {
    this.catalog = updated;
    this.editVisible = false;

    if (updated?.id) {
      this.catalogService.updateCatalogNameCache(updated.id, updated.catalogName);
    }

    if (this.container) {
      this.containerSaved.emit(this.container);
    }
  }

  onMetadataCanceled(): void {
    this.editVisible = false;
  }

 async saveContainerNote(e: any) {
    if (!this.container) return;
    const customerId = this.loginService.currentUser.customerId;
    await this.importContainerService.update(this.container, customerId);
  }
}
